'use client';

import { Icon } from '@/components/ui/Icon';
import { Select } from '@/components/ui/Select';
import { getWristMeasurementUnitFromLocale, useWristSize } from '@/hooks/useWristSize';
import type { StrapSizeVariant } from '@/lib/product/types';
import { modalActions } from '@/states/modalState';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import styles from './StrapSizeSelector.module.scss';

export interface StrapSizeSelectorProps {
  /** All available size variants for this strap */
  sizeVariants: StrapSizeVariant[];
  /** Currently selected size variant */
  selectedSize: StrapSizeVariant | null;
  /** Callback when a size is selected */
  onSizeSelect: (variant: StrapSizeVariant) => void;
  /** Whether to show the size guide button */
  showSizeGuide?: boolean;
  /** Class name for container */
  className?: string;
}

/**
 * Size selector for strap PDPs
 * Uses a dropdown for size selection (matching old solution pattern)
 * with a "Size Guide" link that opens a modal
 */
export function StrapSizeSelector({
  sizeVariants,
  selectedSize,
  onSizeSelect,
  showSizeGuide = true,
  className,
}: Readonly<StrapSizeSelectorProps>) {
  const t = useTranslations('product.strap');
  const params = useParams();
  const locale = (params?.lang as string) || 'us-en';
  const wristMeasurementUnit = getWristMeasurementUnitFromLocale(locale);
  const wristSize = useWristSize(selectedSize?.sizeName, wristMeasurementUnit);

  const handleSizeGuideClick = () => {
    // Open the sizes guide modal
    modalActions.open('sizes-guide', { productType: 'strap' });
  };

  // Translate size code to display name
  const translateSizeName = useCallback(
    (size: string): string => {
      const lowerSize = size.toLowerCase();
      switch (lowerSize) {
        case 'small':
        case 's':
          return t('sizes.small');
        case 'standard':
        case 'medium':
        case 'm':
          return t('sizes.standard');
        case 'large':
        case 'l':
          return t('sizes.large');
        default:
          return size;
      }
    },
    [t]
  );

  // Build dropdown options from size variants
  const sizeOptions = useMemo(() => {
    return sizeVariants.map((variant) => ({
      value: variant.sku,
      label: translateSizeName(variant.sizeName),
    }));
  }, [sizeVariants, translateSizeName]);

  // Handle dropdown change
  const handleSizeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedSku = e.target.value;
      const variant = sizeVariants.find((v) => v.sku === selectedSku);
      if (variant) {
        onSizeSelect(variant);
      }
    },
    [sizeVariants, onSizeSelect]
  );

  if (sizeVariants.length === 0) {
    return null;
  }

  // Straps with a single size keep the dropdown and the Size Guide link: the
  // size is part of what the customer is buying, so the control stays visible
  // even when there is nothing else to pick.
  return (
    <div className={clsx(styles.container, className)}>
      <span className={styles.dropdownLabel}>{t('availableSize')}</span>

      <div className={styles.selectorRow}>
        <Select
          options={sizeOptions}
          value={selectedSize?.sku || ''}
          onChange={handleSizeChange}
          className={styles.sizeDropdown}
          aria-label={t('availableSize')}
        />

        {showSizeGuide && (
          <button type="button" className={styles.sizeGuideButton} onClick={handleSizeGuideClick}>
            <Icon name="ruler" size={16} className={styles.rulerIcon} />
            <span>{t('sizeGuide')}</span>
            <Icon name="chevron-right" size={14} />
          </button>
        )}
      </div>

      <p className={styles.wristSizeText}>
        {t('measuringText', {
          min: wristSize.min,
          max: wristSize.max,
          unit: wristMeasurementUnit,
        })}
      </p>
    </div>
  );
}
